import type { Commitment } from '../../types/api';
import { Eyebrow, Reveal } from '../ui';

const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/** Icône au trait, choisie d'après le titre de l'engagement (qualité, durabilité, innovation, engagement). */
function CommitmentIcon({ title }: { title: string }) {
  const t = title.toLowerCase();
  return (
    <svg viewBox="0 0 48 48" aria-hidden className="h-12 w-12 text-black" {...common}>
      {/\bqualit/.test(t) ? (
        <><rect x="6" y="10" width="30" height="30" rx="2" /><path d="M13 24l7 7 18-20" /></>
      ) : /durab/.test(t) ? (
        <><path d="M24 42V20" /><path d="M24 28c-9 0-13-5-13-12 8 0 13 4 13 12z" /><path d="M24 22c0-7 5-11 13-11 0 7-4 11-13 11z" /><path d="M16 42h16" /></>
      ) : /innov/.test(t) ? (
        <><path d="M24 6a12 12 0 0 0-6 22.5V34h12v-5.5A12 12 0 0 0 24 6z" /><path d="M19 39h10M21 43h6" /></>
      ) : (
        <><path d="M24 6l15 6v11c0 9-6 16-15 19-9-3-15-10-15-19V12z" /><path d="M17 24l5 5 9-10" /></>
      )}
    </svg>
  );
}

const clamp3 = { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as const;

/** « Nos engagements »: titre à gauche, accroche à droite, puis un engagement par colonne (icône, titre, 3 lignes de texte). */
export default function CommitmentColumns({ heading, intro, items }: { heading: string; intro: string; items: Commitment[] }) {
  const list = items.filter((c) => c.enable !== false);
  if (list.length === 0) return null;
  return (
    <section className="bg-surface py-16 text-black md:py-28">
      <div className="site-container">
        <Eyebrow className="mb-4">Nos engagements</Eyebrow>
        <div className="grid gap-6 lg:grid-cols-2 lg:gap-24">
          <Reveal><h2 className="font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal text-black md:text-[2.75rem]">{heading}</h2></Reveal>
          <Reveal delay={0.08}><p className="text-[1.0625rem] leading-relaxed">{intro}</p></Reveal>
        </div>
        <ol className="mt-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {list.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <CommitmentIcon title={c.title} />
              <h3 className="mt-6 font-sans text-[1.375rem] font-semibold normal-case leading-tight tracking-normal text-black">{c.title}</h3>
              <p className="mt-3 text-[1rem] leading-relaxed" style={clamp3}>{c.description}</p>
              {(c.details?.length ?? 0) > 0 && (
                <a href="#engagements-detail" className="mt-5 inline-flex items-center gap-3 text-[0.875rem] font-semibold uppercase tracking-[0.094rem] text-brand-darker transition-colors hover:text-black">
                  En savoir plus<span aria-hidden className="h-px w-9 bg-brand-dark" />
                </a>
              )}
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}
